import { FormGroup } from '@src/components/Form/FormGroup';
import type { Paths } from '@src/utils/types';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { useAppwideDrawerContext } from '../../../../providers';
import type { BoxProps } from '../../../Core/Box';
import type { EntityAdminRecord } from '../../types';
import { isNotDivider, isOptionDivider } from '../InputsAndDropdownsDrawer/components/dividerUtils';
import { DrawerOptionContent } from '../InputsAndDropdownsDrawer/components/DrawerOptionContent';
import { flattenDrawerOptions } from '../InputsAndDropdownsDrawer/filterAndMapDrawerOptions';
import { getOptionLabel } from '../InputsAndDropdownsDrawer/getOptionLabel';
import { getGuideTextValue, getTooltipValue } from '../InputsAndDropdownsDrawer/utils';
import { hideDrawerOption } from '../shared/drawerUtils';
import { useCustomDrawer } from './CustomDrawerContext';
import { useTabContext } from './TabContext';

export interface CustomDrawerFieldControllerProps<TDrawerRecord extends EntityAdminRecord> {
  name: Paths<TDrawerRecord>;
  showLabel?: boolean;
  showTooltip?: boolean;
  /** add-on BoxProps to the FormGroup, beyond defaults to ensure minimal visibility and spacing */
  boxProps?: BoxProps;
}

export function CustomDrawerFieldController<TDrawerRecord extends EntityAdminRecord>({
  name,
  showLabel = true,
  showTooltip = true,
  boxProps: boxPropsAdditional,
}: CustomDrawerFieldControllerProps<TDrawerRecord>) {
  const { form, isEditing, onFormUpdate, inheritedValues, drawerOptions, errorState, touchState, originalEntity } =
    useCustomDrawer<TDrawerRecord>();
  const { isGuideVisible } = useAppwideDrawerContext();
  const { tabName } = useTabContext();
  const warnedRef = useRef<Set<string>>(new Set());

  const drawerOption = useMemo(
    () => flattenDrawerOptions(drawerOptions).find(option => isNotDivider(option) && option.field === name),
    [drawerOptions, name]
  );

  // Validate field registration: check if field is used in the correct tab
  // Must be called before any early returns to satisfy Rules of Hooks
  useEffect(() => {
    // Only validate tab registration if:
    // 1. Field option exists and is valid
    // 2. Field is rendered inside a tab (tabName is not null)
    // 3. Field has registered tabs (if drawerTabs don't exist, field can be used anywhere)
    // 4. At least one field in the drawer has been touched
    // If drawerTabs don't exist on the field, skip tab validation (field can be used in any tab)
    if (
      drawerOption &&
      isNotDivider(drawerOption) &&
      !hideDrawerOption<TDrawerRecord>(drawerOption, form, originalEntity) &&
      tabName &&
      drawerOption.drawerTabs &&
      drawerOption.drawerTabs.length > 0
    ) {
      const hasAnyTouchedField = Object.values(touchState).some(touched => touched);

      if (hasAnyTouchedField && !drawerOption.drawerTabs.includes(tabName)) {
        const warningKey = `${name}-${tabName}`;
        if (!warnedRef.current.has(warningKey)) {
          warnedRef.current.add(warningKey);
          console.warn(
            `Field '${name}' is used in tab '${tabName}' but is registered for tabs: ${drawerOption.drawerTabs.join(
              ', '
            )}`
          );
        }
      }
    }
    // If drawerTabs don't exist, field validation still happens normally (required fields, etc.)
    // but tab registration validation is skipped
  }, [name, tabName, drawerOption, touchState, form, originalEntity]);

  if (
    !drawerOption ||
    isOptionDivider(drawerOption) ||
    hideDrawerOption<TDrawerRecord>(drawerOption, form, originalEntity)
  ) {
    return null;
  }

  // Filter field based on tab assignment: only show if tab is unspecified or correctly specified
  if (tabName && drawerOption.drawerTabs && drawerOption.drawerTabs.length > 0) {
    if (!drawerOption.drawerTabs.includes(tabName)) {
      // Field has a tab assignment that doesn't match the current tab - don't show it
      return null;
    }
  }

  const inheritedValue = inheritedValues[name];
  const label = showLabel ? getOptionLabel(drawerOption, form) : undefined;
  const tooltip: ReactNode | undefined = showTooltip ? getTooltipValue(drawerOption, form) : undefined;
  const guideText: ReactNode | undefined = isGuideVisible ? getGuideTextValue(drawerOption, form) : undefined;
  const boxProps: BoxProps = {
    ...(drawerOption.fillContainerHeight
      ? {
          mb: '0',
          h: '100%',
          w: '100%',
          maxHeight: '100%',
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }
      : { mb: '0', w: '100%' }),
    ...boxPropsAdditional,
  };
  const inline = drawerOption?.inline ?? false;

  const content = (
    <DrawerOptionContent
      drawerOption={drawerOption}
      form={form}
      originalEntity={originalEntity}
      isEditing={isEditing}
      onFormUpdate={onFormUpdate}
      inheritedValue={inheritedValue}
      error={errorState?.[drawerOption.field]}
      touched={touchState[drawerOption.field]}
    />
  );

  // The error is only relevant if we're touched.
  const errorIfTouched = touchState[drawerOption.field] ? errorState?.[drawerOption.field] : undefined;
  return (
    <FormGroup
      tooltip={tooltip}
      label={label}
      guideText={guideText}
      {...boxProps}
      inline={inline}
      error={errorIfTouched}
      useErrorBorder
      alignItems={drawerOption.fillContainerHeight ? 'stretch' : 'center'}
    >
      {content}
    </FormGroup>
  );
}
